import classNames from "classnames";
import React, { HTMLAttributes, InputHTMLAttributes, useState } from "react";
import { Field } from "react-final-form";
import { FiUpload } from "react-icons/fi";
import {
  IoCheckmarkCircle,
  IoEyeOffOutline,
  IoEyeOutline,
} from "react-icons/io5";
import styles from "./input.module.scss";
import { useToast } from "./notifications";

interface IInputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  placeholder: string;
  label: string;
  id: string;
  hasError?: boolean;
  helperText?: React.ReactNode;
  parse?: (value?: string) => any;
}

interface ITextAreaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  placeholder: string;
  label: string;
  id: string;
  hasError?: boolean;
  helperText?: React.ReactNode;
  rows?: number;
}

interface ISelectProps extends HTMLAttributes<HTMLSelectElement> {
  name: string;
  label: string;
  id: string;
  hasError?: boolean;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
  parse?: (value?: string) => any;
}

export function Input({
  id,
  label,
  hasError,
  helperText,
  type,
  ...props
}: IInputProps) {
  const [_type, setType] = useState(() => type);
  return (
    <>
      <div className={classNames(styles.container, styles.error, styles.valid)}>
        <input
          aria-describedby={helperText ? `${id}-descr` : undefined}
          type={_type}
          id={id}
          required
          {...props}
        />
        <label htmlFor={id}>{label}</label>
        {type === "password" && (
          <button
            onClick={(e) => {
              setType((type) => (type === "password" ? "text" : "password"));
              console.log(styles.container);
              (e.currentTarget.closest("div") as HTMLDivElement)
                ?.querySelector("input")
                ?.focus?.();
            }}
            className={styles.eye}
            type="button"
            title="tpggle password reveal"
          >
            {_type === "password" ? <IoEyeOutline /> : <IoEyeOffOutline />}
          </button>
        )}
        <IoCheckmarkCircle className={styles.valid} />
      </div>
      {!!helperText && <small id={`${id}-descr`}>{helperText}</small>}
    </>
  );
}

export function InputField({
  id,
  label,
  hasError,
  helperText,
  type,
  ...props
}: IInputProps) {
  const [_type, setType] = useState(() => type);
  return (
    <>
      <div
        className={classNames(styles.container, { [styles.error]: hasError })}
      >
        <Field
          component="input"
          aria-describedby={helperText ? `${id}-descr` : undefined}
          type={_type}
          id={id}
          required
          {...props}
        />
        <label htmlFor={id}>{label}</label>
        {type === "password" && (
          <button
            onClick={(e) => {
              setType((type) => (type === "password" ? "text" : "password"));
              (e.currentTarget.closest("div") as HTMLDivElement)
                ?.querySelector("input")
                ?.focus?.();
            }}
            className={styles.eye}
            type="button"
            title="tpggle password reveal"
          >
            {_type === "password" ? <IoEyeOutline /> : <IoEyeOffOutline />}
          </button>
        )}
        <IoCheckmarkCircle className={styles.valid} />
      </div>
      {!!helperText && <small id={`${id}-descr`}>{helperText}</small>}
    </>
  );
}

export function FileField({
  id,
  label,
  hasError,
  helperText,
  type,
  accept,
  ...props
}: IInputProps) {
  const toast = useToast();
  function isFileTypeAccepted(file: File, specifiedFiles?: string): boolean {
    if (!specifiedFiles) {
      return true; // If accept is empty, all file types are allowed
    }
    if (!file) {
      return false;
    }
    // Split the specified file types (e.g., ".png, .jpg, .jpeg") into an array
    const acceptedTypes = specifiedFiles
      .split(",")
      .map((type) => type.trim().toLowerCase());

    // Get the file's extension or MIME type
    const fileExtension = file.name.includes(".")
      ? `.${file.name.split(".").pop()?.toLowerCase()}`
      : undefined;

    const fileType = file.type.toLowerCase();

    // Check if the file matches any of the accepted types
    return acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        // Match by file extension
        return fileExtension === type;
      } else {
        // Match by MIME type
        return fileType === type;
      }
    });
  }
  return (
    <>
      <div
        className={classNames(styles.container, { [styles.error]: hasError })}
      >
        <Field
          component="input"
          aria-describedby={helperText ? `${id}-descr` : undefined}
          type="file"
          id={id}
          required
          name={props.name}
        >
          {({ input: { value, ...input } }) => (
            <input
              type="file"
              id={id}
              required={!value}
              className="sr-only"
              accept={accept}
              {...input}
              onChange={(e) => {
                const files = e.target.files;
                const file = files && files[0];
                if (file) {
                  if (isFileTypeAccepted(files[0], accept)) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      input.onChange(e.target?.result);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    e.target.files = null;
                    e.target.value = "";
                    toast({
                      type: "warning",
                      text: "Invalid file",
                    });
                  }
                }
              }}
              {...props}
            />
          )}
        </Field>

        <label htmlFor={id}>
          <FiUpload size={32} />
          {label}
        </label>
        <IoCheckmarkCircle className={styles.valid} />
      </div>
      {!!helperText && <small id={`${id}-descr`}>{helperText}</small>}
    </>
  );
}

export function TextAreaField({
  id,
  label,
  hasError,
  helperText,
  ...props
}: ITextAreaProps) {
  return (
    <>
      <div
        className={classNames(styles.container, { [styles.error]: hasError })}
      >
        <Field
          component="textarea"
          aria-describedby={helperText ? `${id}-descr` : undefined}
          id={id}
          {...props}
        />
        <label htmlFor={id}>{label}</label>
        <IoCheckmarkCircle className={styles.valid} />
      </div>
      {!!helperText && <small id={`${id}-descr`}>{helperText}</small>}
    </>
  );
}

export function SelectField({
  id,
  label,
  hasError,
  helperText,
  ...props
}: ISelectProps) {
  return (
    <>
      <div
        className={classNames(styles.container, { [styles.error]: hasError })}
      >
        <Field
          component="select"
          aria-describedby={helperText ? `${id}-descr` : undefined}
          id={id}
          {...props}
        />
        <label htmlFor={id}>{label}</label>
      </div>
      {!!helperText && <small id={`${id}-descr`}>{helperText}</small>}
    </>
  );
}

export function Select({
  id,
  label,
  hasError,
  helperText,
  ...props
}: ISelectProps) {
  return (
    <>
      <div
        className={classNames(styles.container, { [styles.error]: hasError })}
      >
        <select
          aria-describedby={helperText ? `${id}-descr` : undefined}
          id={id}
          {...props}
        />
        <label htmlFor={id}>{label}</label>
      </div>
      {!!helperText && <small id={`${id}-descr`}>{helperText}</small>}
    </>
  );
}
